import { useState, useEffect, useCallback } from "react";
import { ScrollText, User as UserIcon } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const PAGE = 50;

function timeAgo(iso) {
  const d = new Date(iso);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" }) + " " + d.toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" });
}

export const AdminActivity = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (skip = 0) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/logs?limit=${PAGE}&skip=${skip}`);
      setTotal(data.total);
      setLogs((prev) => (skip === 0 ? data.logs : [...prev, ...data.logs]));
    } catch { /* super admin only */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(0); }, [load]);

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <ScrollText className="w-5 h-5 text-[#8C7B6B]" />
        <p className="text-sm text-[#6E675F]" data-testid="admin-activity-count">
          <strong className="text-[#1A1A1A]">{total}</strong> logged admin action{total === 1 ? "" : "s"} — newest first.
        </p>
      </div>

      {logs.length === 0 && !loading ? (
        <div className="text-center py-20 border border-dashed border-[#DDD7CF] rounded-lg" data-testid="admin-activity-empty">
          <p className="text-[#6E675F]">No admin activity recorded yet. Actions on products, orders, settings, coupons, users and bundles will appear here.</p>
        </div>
      ) : (
        <div className="border border-[#DDD7CF] rounded-lg bg-white overflow-x-auto">
          <Table data-testid="admin-activity-table">
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Admin</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((l) => (
                <TableRow key={l.id} data-testid={`admin-log-row-${l.id}`}>
                  <TableCell className="whitespace-nowrap text-xs text-[#6E675F]">{timeAgo(l.created_at)}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5 text-sm">
                      <UserIcon className="w-3.5 h-3.5 text-[#8C7B6B]" />
                      <span className="font-medium">{l.admin_name}</span>
                    </span>
                    <span className="block text-xs text-[#6E675F]">{l.admin_email}</span>
                  </TableCell>
                  <TableCell className="text-sm font-medium">{l.action}</TableCell>
                  <TableCell className="text-sm text-[#6E675F]">
                    {l.target}{l.details ? <span className="text-xs block">{l.details}</span> : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {logs.length < total && (
        <div className="text-center mt-6">
          <Button variant="outline" disabled={loading} onClick={() => load(logs.length)} data-testid="admin-activity-load-more">
            {loading ? "Loading…" : "Load More"}
          </Button>
        </div>
      )}
    </div>
  );
};
